// WHAT: React hook for fetching and managing Yazio consumed items data
// WHY:  Provides state management and API integration for retrieving daily food consumption data from Yazio
// DOMAIN: Health → Yazio → Nutrition Tracking
// DEPENDENCIES: Tauri core API, React hooks, Yazio backend service
// RISKS:
//   - NOTE: No auto-load on mount - requires manual trigger which may cause UX confusion
//   - NOTE: Error messages are generic - may not help debug specific API failures
//   - WARN: Date parameter defaults to today but format validation is minimal
//   - NOTE: State management doesn't handle concurrent requests - may cause race conditions

import { core } from "@tauri-apps/api";
import { useState } from "react";

export interface YazioConsumedItemsRequest {
  date?: string; // ISO date string, defaults to today
}

export interface YazioConsumedItem {
  amount: number;
  calories: number;
  daytime: string;
  id: string;
  name: string;
  productId: string;
  serving?: string;
  servingQuantity?: number;
  type: string;
}

export interface YazioConsumedItemsResponse {
  date: string;
  items: YazioConsumedItem[];
}

interface YazioConsumedItemsState {
  data: undefined | YazioConsumedItemsResponse;
  error: string | undefined;
  isLoading: boolean;
  lastUpdated: Date | undefined;
}

/**
 * WHAT:
 *   React hook for fetching and managing Yazio consumed items data.
 *
 * WHY:
 *   Provides state management and API integration for retrieving daily food consumption data from Yazio.
 *   Allows manual control over data loading to prevent errors when credentials are not configured.
 *
 * @returns Object containing:
 *   - data: YazioConsumedItemsResponse | undefined — Consumed items data
 *   - error: string | undefined — Error message if fetch failed
 *   - isLoading: boolean — Loading state
 *   - lastUpdated: Date | undefined — Timestamp of last successful fetch
 *   - clearError: () => void — Clear error state
 *   - fetchConsumedItems: (request?: YazioConsumedItemsRequest) => Promise<void> — Fetch data for specific date
 *   - loadDateData: (date: string) => void — Load data for specific date
 *   - loadTodayData: () => void — Load today's data
 *   - refreshData: () => void — Refresh current data
 *
 */
export function useYazioConsumedItems() {
  const [state, setState] = useState<YazioConsumedItemsState>({
    data: undefined,
    error: undefined,
    isLoading: false,
    lastUpdated: undefined,
  });

  const fetchConsumedItems = async (request: YazioConsumedItemsRequest = {}) => {
    try {
      setState((previous) => ({ ...previous, error: undefined, isLoading: true }));

      const data = await core.invoke<YazioConsumedItemsResponse>("get_yazio_consumed_items", {
        date: request.date,
      });

      setState((previous) => ({
        ...previous,
        data,
        isLoading: false,
        lastUpdated: new Date(),
      }));
    } catch {
      // NOTE: Failed to fetch consumed items
      setState((previous) => ({
        ...previous,
        error: "Failed to load consumed items data",
        isLoading: false,
      }));
    }
  };

  const refreshData = () => {
    if (state.data) {
      void fetchConsumedItems({ date: state.data.date });
    } else {
      void fetchConsumedItems();
    }
  };

  const loadTodayData = () => {
    void fetchConsumedItems();
  };

  const loadDateData = (date: string) => {
    void fetchConsumedItems({ date });
  };

  const clearError = () => {
    setState((previous) => ({ ...previous, error: undefined }));
  };

  // Don't auto-load on mount - let user manually trigger data loading
  // This prevents errors when credentials are not configured

  return {
    ...state,
    clearError,
    fetchConsumedItems,
    loadDateData,
    loadTodayData,
    refreshData,
  };
}
